import {
  setupMultiApiClient,
  createTokenStorage,
  createHeaderRule,
  getEndpointsForEnv,
} from "@oneapp/data";
import { createSelfLearnApiConfig } from "./buildEndpoints";
import {
  defaultSelfLearnSession,
  type SelfLearnSession,
} from "./selfLearnSession";

/** Response from login-with-OTP. Persisted as accountsData. */
export interface AccountsData {
  access_token: string;
  refresh_token: string;
  is_user_existing: boolean;
  unified_accounts: {
    unified_account_id: string;
    first_name: string;
    last_name: string;
    grade?: number;
    user_account_photo?: string | null;
  }[];
}

/** Profile item within unified_account_details.profiles. */
export interface ProfileItem {
  uam_id?: number;
  admission_id?: string;
  crn_id?: string;
  branch_id?: string;
  tenant_id?: string;
  vertical_id?: number;
  is_default_profile?: boolean;
  is_temp_password?: boolean;
  tenant_code?: string;
  exams?: { examId: number; name: string }[];
  [key: string]: unknown;
}

/** Response from profiles API. Persisted as profilesData. */
export interface ProfilesData {
  access_token?: string;
  refresh_token?: string;
  response?: string;
  unified_account_details: {
    unified_account_id?: string;
    profiles?: ProfileItem[];
    user_account_details?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

/** Single program from enrolled-programs API. Persisted as enrolledPrograms. */
export interface EnrolledProgram {
  program_id?: string;
  program_name?: string;
  gradeId?: number;
  examId?: number;
  gradeName?: string;
  examName?: string;
  batch?: string | null;
  tenant_id?: string;
  subtenant_id?: number;
  vertical_id?: number;
  subjects?: unknown[];
  mdsExamName?: string;
  mdsExamId?: number;
  mdsGradeName?: string;
  mdsGradeId?: number;
  duration_in_weeks?: number;
  mdsOfferingName?: string;
  mdsOfferingId?: number;
  programOfferingIds?: number[];
  isDefault?: boolean;
  [key: string]: unknown;
}

export interface ModuleDataDto {
  /** Authenticated country code used by API validation (e.g. IND). */
  countryCode?: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  accountsData: AccountsData | null;
  profilesData: ProfilesData | null;
  enrolledPrograms: EnrolledProgram[] | null;
  /** Optional; when set, forwarded onto {@link SelfLearnSession.setSessionData}. */
  setSessionData?: (
    tenantId: string | null,
    subtenantId: number | null,
    programs?: EnrolledProgram[] | null,
  ) => void;
}

export const defaultModuleDataDto: ModuleDataDto = {
  accessToken: null,
  refreshToken: null,
  accountsData: null,
  profilesData: null,
  enrolledPrograms: null,
};

/** Same resolution as {@link moduleDataDtoToSelfLearnSession}: `isDefault` row, else index `0`. */
export function getPrimaryEnrolledProgram(
  enrolledPrograms: EnrolledProgram[] | null | undefined,
): EnrolledProgram | undefined {
  return enrolledPrograms?.find((p) => p.isDefault) ?? enrolledPrograms?.[0];
}

/** Puts `selected` at index 0 (host `setEnrolledPrograms` treats `[0]` as default). */
export function mergeEnrolledProgramsWithDefaultSelection(
  enrolled: EnrolledProgram[] | null | undefined,
  selected: EnrolledProgram & { program_id: string },
): EnrolledProgram[] {
  const pid = selected.program_id;
  const list = enrolled?.length ? enrolled.map((p) => ({ ...p })) : [];
  const idx = list.findIndex((p) => p.program_id === pid);
  const row: EnrolledProgram =
    idx === -1
      ? { ...selected, isDefault: true }
      : { ...list[idx], ...selected, isDefault: true };
  const rest = list
    .filter((p) => p.program_id !== pid)
    .map((p) => ({ ...p, isDefault: false }));
  return [row, ...rest];
}

/** Same key as `SelfLearnHomePage` / player pages — persisted course selection for header/session sync. */
export const SELF_LEARN_SELECTED_PROGRAM_STORAGE_KEY =
  "SELF_LEARN_SELECTED_PROGRAM";

/** Reads `program_id` from persisted self-learn course selection (if parseable). */
export function readSelfLearnSelectedProgramIdFromStorage():
  | string
  | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(SELF_LEARN_SELECTED_PROGRAM_STORAGE_KEY);
    if (!raw) return undefined;
    const saved = JSON.parse(raw) as { program_id?: string } | null;
    const id = saved?.program_id;
    return id != null && String(id).trim().length > 0 ? String(id) : undefined;
  } catch {
    return undefined;
  }
}

/** Course picker: persisted choice, then enrollment primary, then first program. */
export function resolveSelfLearnPickerSelectedProgramId(
  programsForPicker: { program_id: string }[],
  enrolledPrograms?: EnrolledProgram[] | null,
): string | undefined {
  if (programsForPicker.length === 0) return undefined;

  const isValid = (id: string | undefined): id is string =>
    id != null &&
    programsForPicker.some((p) => String(p.program_id) === String(id));

  const fromStorage = readSelfLearnSelectedProgramIdFromStorage();
  if (isValid(fromStorage)) return fromStorage;

  const primaryId = getPrimaryEnrolledProgram(enrolledPrograms)?.program_id;
  if (isValid(primaryId)) return String(primaryId);

  return String(programsForPicker[0].program_id);
}

const noOpTokenStorage = createTokenStorage({
  getAccessToken: () => null,
  getRefreshToken: () => null,
  setTokens: () => {},
  clearTokens: () => {},
});

/** Maps host {@link ModuleDataDto} into the slim session used for API headers (same idea as mobile `getSelfLearnSession`). */
export function moduleDataDtoToSelfLearnSession(
  dto: ModuleDataDto,
): SelfLearnSession {
  const enrolledPrograms = dto.enrolledPrograms;
  const program = getPrimaryEnrolledProgram(enrolledPrograms);
  const profile = dto.profilesData?.unified_account_details?.profiles?.[0];
  const ua = dto.accountsData?.unified_accounts?.[0];
  const phone =
    (dto.profilesData?.unified_account_details?.user_account_details?.phone as
      | string
      | undefined) ?? null;

  const rawExam = Number(program?.examId ?? program?.mdsExamId ?? 0);
  const rawGrade = Number(program?.mdsGradeId ?? program?.gradeId ?? 0);
  const rawMdsExam = Number(program?.mdsExamId ?? 0);
  const rawMdsGrade = Number(program?.mdsGradeId ?? 0);
  const rawSub = program?.subtenant_id;
  const setSessionData = dto.setSessionData;

  return {
    accessToken: dto.accessToken ?? null,
    countryCode: dto.countryCode ?? null,
    refreshToken: dto.refreshToken ?? null,
    gradeId: Number.isFinite(rawGrade) && rawGrade > 0 ? rawGrade : null,
    mdsGradeId:
      Number.isFinite(rawMdsGrade) && rawMdsGrade > 0 ? rawMdsGrade : null,
    examId: Number.isFinite(rawExam) && rawExam > 0 ? rawExam : null,
    examName: program?.examName ?? program?.mdsExamName ?? null,
    mdsExamId:
      Number.isFinite(rawMdsExam) && rawMdsExam > 0 ? rawMdsExam : null,
    mdsExamName: program?.mdsExamName ?? null,
    tenantId: program?.tenant_id ?? null,
    subtenantId:
      rawSub != null && Number.isFinite(Number(rawSub)) ? Number(rawSub) : null,
    firstName: ua?.first_name ?? null,
    lastName: ua?.last_name ?? null,
    phone,
    uamId: profile?.uam_id ?? null,
    verticalId: program?.vertical_id ?? profile?.vertical_id ?? null,
    unifiedAccountId:
      dto.profilesData?.unified_account_details?.unified_account_id ??
      ua?.unified_account_id ??
      null,
    programId: program?.program_id ?? null,
    enrolledPrograms: enrolledPrograms ?? null,
    isdCode:
      (dto.profilesData?.unified_account_details?.user_account_details
        ?.isd_code as string | undefined) ?? null,
    setSessionData: setSessionData
      ? (tenantId, subtenantId) => setSessionData(tenantId, subtenantId)
      : () => {},
  };
}

function buildHeaderRules(session: SelfLearnSession = defaultSelfLearnSession) {
  const getTenant = (): string => session.tenantId ?? "infinitylearn";
  const getDeviceId = (): string =>
    (typeof localStorage !== "undefined" && localStorage.getItem("deviceIp")) ||
    "127.0.0.1";
  const getUserId = (): string | null =>
    session.uamId != null ? String(session.uamId) : null;
  const getUserDetails = (): {
    subTenant?: string | number;
    tenantId?: string | number;
  } | null =>
    session.subtenantId != null || session.tenantId != null
      ? {
          tenantId: session.tenantId ?? undefined,
          subTenant: session.subtenantId ?? undefined,
        }
      : null;
  const getAccessToken = (): string | null => session.accessToken ?? null;
  const getRefreshToken = (): string | null => session.refreshToken ?? null;

  return [
    createHeaderRule({
      pattern: /one_asm_api|one_asm_asset_api|proctoringUrl|geojs/,
      remove: [
        "X-Tenant",
        "ot-tenant",
        "X-Platform",
        "X-DeviceId",
        "Authorization",
      ],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /api\.oneapp\.com/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /infinitylearn\.com|devinfinitylearn\.in/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        accesstoken: () => getAccessToken(),
        Authorization: () => {
          const t = getAccessToken();
          return t ? `Bearer ${t}` : null;
        },
      },
      priority: 50,
    }),
    // Match real SL hosts (v2) and any path under /api/selflearn/ — the old `/selflearnapi/`
    // substring never matched `selflearnv2stgapi.*` / `selflearnv2api.*` URLs from env-endpoints.
    createHeaderRule({
      pattern:
        /selflearnapi\.devinfinitylearn\.net|selflearnv2(stg)?api\.(devinfinitylearn\.in|infinitylearn\.com)|\/api\/selflearn\//,
      add: {
        country_code: () => session.countryCode ?? null,
        subTenantId: () => {
          const d = getUserDetails();
          return d?.subTenant != null ? String(d.subTenant) : "";
        },
        tenantId: () => {
          const d = getUserDetails();
          return d?.tenantId != null ? String(d.tenantId) : "";
        },
        userid: () => getUserId(),
        accesstoken: () => getAccessToken(),
        Authorization: () => {
          const t = getAccessToken();
          return t ? `Bearer ${t}` : null;
        },
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern:
        /oneapp(staginglambda|api)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
      add: {
        country_code: () => session.countryCode ?? null,
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        platform: "web",
        "X-DeviceId": () => getDeviceId(),
        accesstoken: () => getAccessToken(),
        Authorization: () => {
          const t = getAccessToken();
          return t ? `Bearer ${t}` : null;
        },
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /cms.*api|communityapi|onecms/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        platform: "web",
        "X-DeviceId": () => getDeviceId(),
        isMultiSessionRequired: "true",
        userid: () => getUserId(),
        accesstoken: () => getAccessToken(),
        Authorization: () => {
          const t = getAccessToken();
          return t ? `Bearer ${t}` : null;
        },
        subTenantId: () => {
          const d = getUserDetails();
          return d?.subTenant != null ? String(d.subTenant) : "";
        },
        tenantId: () => {
          const d = getUserDetails();
          return d?.tenantId != null ? String(d.tenantId) : "";
        },
        Accept: "application/json",
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern: /api\/selflearn\/getMapping/,
      remove: ["X-Tenant", "ot-tenant", "X-Platform", "X-DeviceId"],
      priority: 100,
    }),
    createHeaderRule({
      pattern: /myactivity\.devinfinitylearn\.in/,
      add: {
        "X-Tenant": () => getTenant(),
        "ot-tenant": () => getTenant(),
        "X-Platform": "web",
        "X-DeviceId": () => getDeviceId(),
        accesstoken: () => getAccessToken(),
        refreshtoken: () => getRefreshToken(),
      },
      priority: 50,
    }),
    createHeaderRule({
      pattern:
        /(lsqmanagement(preprod)?\.devinfinitylearn\.in|lsqmanagementservice\.infinitylearn\.com)/,
      add: {
        Accept: "application/json",
        "Content-Type": "application/json",
        platform: "web",
        Authorization: () => {
          const t = getAccessToken();
          return t ? `Bearer ${t}` : null;
        },
      },
      priority: 50,
    }),

    createHeaderRule({
      pattern: /lsqservice\.infinitylearn\.com/,
      add: {
        Accept: "application/json",
        "Content-Type": "application/json",
        platform: "web",
      },
      priority: 50,
    }),
  ];
}

export function initializeApi(
  env: string = "production",
  dataDto: ModuleDataDto = defaultModuleDataDto,
): void {
  const session = moduleDataDtoToSelfLearnSession(dataDto);
  Object.assign(defaultSelfLearnSession, session);
  const apiEndpoints = getEndpointsForEnv(env);

  setupMultiApiClient({
    apiConfig: createSelfLearnApiConfig(apiEndpoints),
    tokenStorage: noOpTokenStorage,
    refreshTokenFn: async () => {
      throw new Error("SelfLearn module: no refresh token configured");
    },
    onAuthFailure: () => {},
    onError: () => {},
    enableLogging: env === "development" || env === "staging",
    retry: {
      count: 2,
      delay: 1000,
      statusCodes: [408, 500, 502, 503, 504],
    },
    timeout: 30000,
    crossTab: { enabled: false },
    headerRules: buildHeaderRules(session),
  });
}
